import React, { useEffect, useRef, useState } from "react";
import { useProfile, useTimer, useTasks } from "../hooks/useStudyOS";
import { logFocusSession } from "../lib/db";
import { useLocation } from "react-router-dom";

export default function FocusScreen() {
  const { profile } = useProfile();
  const { pending } = useTasks();
  const {
    running, fraction, display, mode,
    toggle, reset, switchMode, isComplete
  } = useTimer();
  const location = useLocation();
  const passedSubject = location.state?.subject || null;
  const CIRC = 603.2;
  const hasCompletedRef = useRef(false);
  const [selectedTaskId, setSelectedTaskId] = useState("");

  // Request notification permission
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Handle session complete
  useEffect(() => {
    if (isComplete && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const mins = mode === "pomodoro" ? 25 : mode === "deep" ? 45 : 5;
      logFocusSession(mins, mode, selectedTaskId || null).catch(console.error);
      playSound();
      sendNotification(mode);
    }
    if (!isComplete) {
      hasCompletedRef.current = false;
    }
  }, [isComplete, mode, selectedTaskId]);

  const playSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const beep = (delay) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.5);
      };
      beep(0); beep(0.3); beep(0.6);
    } catch (e) {
      console.warn("Audio error:", e);
    }
  };

  const sendNotification = (mode) => {
    try {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const messages = {
        pomodoro: { title: "Focus session complete! 🎉", body: "Great work! Take a 5-minute break." },
        deep:     { title: "Deep work session done! 💪", body: "Excellent focus. Time to rest." },
        break:    { title: "Break over! ⏱", body: "Ready to get back to work?" },
      };
      const msg = messages[mode] || messages.pomodoro;
      new Notification(msg.title, { body: msg.body });
    } catch (e) {
      console.warn("Notification error:", e);
    }
  };

  const modes = [
    { label: "Pomodoro 25m", mins: 25, key: "pomodoro" },
    { label: "Deep work 45m", mins: 45, key: "deep" },
    { label: "Break 5m", mins: 5, key: "break" },
  ];

  const selectedTask = pending.find(t => t.id === selectedTaskId);

  return (
    <div className="screen-wrap" style={{display:"flex", flexDirection:"column", alignItems:"center"}}>

      <div style={styles.modes}>
        {modes.map(m => (
          <button
            key={m.key}
            style={{...styles.modeBtn, ...(mode === m.key ? styles.modeBtnActive : {})}}
            onClick={() => switchMode(m.mins, m.key)}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Task selector */}
      <div style={styles.taskSelector}>
        <label style={styles.taskSelectorLabel}>Working on:</label>
        <select
          style={styles.taskSelect}
          value={selectedTaskId}
          onChange={e => setSelectedTaskId(e.target.value)}
          disabled={running}>
          <option value="">— Select a task (optional) —</option>
          {pending.filter(t => t.type === "deep" || !t.type).map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
          {pending.filter(t => t.type === "quick").length > 0 && (
            <>
              <option disabled>── Quick tasks ──</option>
              {pending.filter(t => t.type === "quick").map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </>
          )}
        </select>
      </div>

      <div style={styles.ringWrap}>
        <svg width="220" height="220" viewBox="0 0 220 220" style={{position:"absolute",inset:0}}>
          <circle cx="110" cy="110" r="96" fill="none" stroke="var(--surface2)" strokeWidth="10"/>
          <circle
            cx="110" cy="110" r="96"
            fill="none" stroke="var(--accent)" strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - fraction)}
            style={{
              transformOrigin:"center",
              transform:"rotate(-90deg)",
              transition:"stroke-dashoffset .9s linear",
              WebkitTransformOrigin:"110px 110px",
              WebkitTransform:"rotate(-90deg)",
            }}
          />
        </svg>
        <div style={styles.ringCenter}>
          <div style={styles.timeDisplay}>{display()}</div>
          <div style={styles.phaseLabel}>
            {isComplete ? "Complete! 🎉" : running ? "Stay focused!" : "Ready"}
          </div>
          <div style={styles.subjLabel}>
            {selectedTask ? selectedTask.name : passedSubject || (mode === "pomodoro" ? "Pomodoro" : mode === "deep" ? "Deep work" : "Break")}
          </div>
        </div>
      </div>

      <div style={styles.controls}>
        <button style={styles.ctrlBtn} onClick={reset}>↺</button>
        <button style={styles.playBtn} onClick={toggle}>
          {running ? "⏸" : "▶"}
        </button>
        <button style={styles.ctrlBtn} onClick={() => {
          switchMode(mode === "pomodoro" ? 25 : mode === "deep" ? 45 : 5, mode);
        }}>⏭</button>
      </div>

      {/* Session logged confirmation */}
      {isComplete && selectedTask && (
        <div style={styles.sessionLogged}>
          ✅ Session logged for <strong>{selectedTask.name}</strong>
        </div>
      )}

      <div style={styles.sessGrid}>
        <div style={styles.sessCard}>
          <div style={styles.sessVal}>{profile?.totalSessions || 0}</div>
          <div style={styles.sessLbl}>Sessions</div>
        </div>
        <div style={styles.sessCard}>
          <div style={styles.sessVal}>
            {profile?.totalFocusMins
              ? `${Math.floor(profile.totalFocusMins/60)}h ${profile.totalFocusMins%60}m`
              : "0m"}
          </div>
          <div style={styles.sessLbl}>Focused</div>
        </div>
        <div style={styles.sessCard}>
          <div style={styles.sessVal}>{profile?.timerStreak || 0}</div>
          <div style={styles.sessLbl}>Streak 🔥</div>
        </div>
      </div>

      <div style={styles.streakPill}>
        <span style={{fontSize:"18px"}}>🔥</span>
        <span style={styles.streakTxt}>
          {profile?.timerStreak
            ? `${profile.timerStreak}-day study streak!`
            : "Start your first session!"}
        </span>
      </div>

      <div style={styles.notifNote}>
        {typeof Notification !== "undefined" && Notification.permission === "granted"
          ? "🔔 Notifications enabled"
          : "🔔 Allow notifications to get alerted when timer ends"}
      </div>
    </div>
  );
}

const styles = {
  modes:           { display:"flex", gap:"8px", marginBottom:"20px", flexWrap:"wrap", justifyContent:"center", paddingTop:"20px" },
  modeBtn:         { padding:"8px 16px", borderRadius:"20px", fontSize:"13px", fontWeight:500, border:"1px solid var(--border2)", cursor:"pointer", background:"transparent", fontFamily:"inherit", color:"var(--text2)" },
  modeBtnActive:   { background:"var(--accent)", borderColor:"var(--accent)", color:"#fff" },
  taskSelector:    { width:"100%", marginBottom:"20px", display:"flex", flexDirection:"column", gap:"6px" },
  taskSelectorLabel:{ fontSize:"12px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em" },
  taskSelect:      { width:"100%", padding:"11px 14px", background:"var(--surface)", border:"1px solid var(--border2)", borderRadius:"10px", fontFamily:"inherit", fontSize:"14px", color:"var(--text)", outline:"none", cursor:"pointer" },
  ringWrap:        { position:"relative", width:"220px", height:"220px", marginBottom:"24px" },
  ringCenter:      { position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" },
  timeDisplay:     { fontSize:"48px", fontWeight:300, color:"var(--text)", letterSpacing:"-2px", fontFamily:"monospace", lineHeight:1 },
  phaseLabel:      { fontSize:"13px", color:"var(--text2)", marginTop:"5px", fontWeight:500 },
  subjLabel:       { fontSize:"12px", color:"var(--accent)", marginTop:"3px", fontWeight:500, textAlign:"center", padding:"0 20px" },
  controls:        { display:"flex", gap:"14px", alignItems:"center", marginBottom:"16px" },
  ctrlBtn:         { width:"48px", height:"48px", borderRadius:"50%", border:"1px solid var(--border2)", background:"var(--surface)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", color:"var(--text)" },
  playBtn:         { width:"68px", height:"68px", borderRadius:"50%", background:"var(--accent)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"26px", color:"#fff", boxShadow:"0 0 30px var(--accent-bg)" },
  sessionLogged:   { fontSize:"13px", color:"var(--green)", background:"var(--green-bg)", border:"1px solid var(--green)", borderRadius:"10px", padding:"10px 16px", marginBottom:"16px", textAlign:"center", width:"100%" },
  sessGrid:        { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px", width:"100%", marginBottom:"16px" },
  sessCard:        { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"12px", padding:"12px", textAlign:"center" },
  sessVal:         { fontSize:"20px", fontWeight:700, color:"var(--text)" },
  sessLbl:         { fontSize:"11px", color:"var(--text3)", marginTop:"2px" },
  streakPill:      { display:"inline-flex", alignItems:"center", gap:"8px", background:"var(--amber-bg)", border:"1px solid rgba(240,164,41,.2)", borderRadius:"20px", padding:"8px 18px", marginBottom:"16px" },
  streakTxt:       { fontSize:"13px", fontWeight:600, color:"var(--amber)" },
  notifNote:       { fontSize:"12px", color:"var(--text3)", textAlign:"center", padding:"8px 0" },
};